const { String } = require('../String/String')
const { is } = require('../is/is')

const { getCurrentDateWithFirstDay } = require('./utils/getCurrentDateWithFirstDay')
const { getDatePartsBetweenDates } = require('./utils/getDatePartsBetweenDates')
const { getDaysCountBetweenDates } = require('./utils/getDaysCountBetweenDates')
const { getYearsBetweenDates } = require('./utils/getYearsBetweenDates')
const { getWeeksBetweenDates } = require('./utils/getWeeksBetweenDates')
const { getDaysBetweenDates } = require('./utils/getDaysBetweenDates')
const { addSubDaysFullDate } = require('./utils/addSubDaysFullDate')
const { addSubDaysDate } = require('./utils/addSubDaysDate')
const { getFullDate } = require('./utils/getFullDate')
const { isValidDate } = require('./utils/isValidDate')
const { addSubDays } = require('./utils/addSubDays')
const { getDiffMs } = require('./utils/getDiffMs')
const { getDate } = require('./utils/getDate')
const { getYear } = require('./utils/getYear')
const { getTime } = require('./utils/getTime')

const SECOND_IN_MS = 1000
const MINUTE_IN_MS = 60 * SECOND_IN_MS
const HOUR_IN_MS   = 60 * MINUTE_IN_MS
const DAY_IN_MS    = 24 * HOUR_IN_MS
const WEEK_IN_MS   =  7 * DAY_IN_MS
const MONTH_IN_MS  = 30 * DAY_IN_MS
const DAYS_OF_WEEK = 7
const DAYS_OF_YEAR = 365
const SECONDS_IN_HOUR = 3600

const UNIX_EPOCH = new Date('1 January 1970 00:00:00 UTC')
const UTC_OFFSET = UNIX_EPOCH.getTimezoneOffset()

/*
const ONE_MIN_IN_SECS  = 60
const ONE_HOUR_IN_MINS = 60
const ONE_DAY_IN_HOURS = 24
const ONE_DAY_IN_SECS  = ONE_DAY_IN_HOURS * ONE_HOUR_IN_MINS * ONE_MIN_IN_SECS
*/

/** @type {readonly string[]} */
const MONTHS = [
  'December',
  'Január',
  'Február',
  'Március',
  'Április',
  'Május',
  'Június',
  'Július',
  'Augusztus',
  'Szeptember',
  'Október',
  'November'
]

class DateTime {
  /**
   * Visszaadja a megadott dátum ISO megfelelőjét.
   * @param {unknown} value - Az ismeretlen érték.
   * @returns {string} ISO dátum.
   */
  static toISOString (value) {
    if (!DateTime.isValidDate(value)) {
      return ''
    }

    const str = is.string(value) ? value : ''

    return (new Date(str))
      .toISOString()
      .replace(/T/, ' ')
      .replace(/\..+/, '')
  }

  /**
   * Visszaadja az aktuális év-hónap-napot.
   * @returns {string} Az ISO dátum: YYYY-mm-dd.
   */
  static getCurrentDate () {
    return DateTime.getDate()
  }

  /**
   * Visszaadja az aktuális időt..
   * @returns {string} Az ISO idő: hh:ii:ss.
   */
  static getCurrentTime () {
    return DateTime.getTime()
  }

  /**
   * Visszaadja az aktuális dátumot és időt.
   * @param {string} separator - A szeparátor a dátum és az idő között.
   * @returns {string} Az ISO teljes dátum: YYYY-mm-dd hh:ii:ss.
   */
  static getCurrentDateAndTime (separator = ' ') {
    const date = DateTime.getCurrentDate()
    const time = DateTime.getCurrentTime()

    return `${ date }${ separator }${ time }`
  }

  /**
   * Visszaadja a `min` és `max` közötti órákat.
   * @param {string} min - A minimum DateTime.
   * @param {string} max - A maximum DateTime.
   * @returns {ReadonlyArray<string>} A megadott DateTime-ok közötti órák.
   */
  static getHoursBetweenDateTimes (min, max) {
    /**
     * A `date` időpontból kiveszi a dátumot és az órák számát.
     * @param {Date} date - Az aktuális időpont.
     * @returns {string} A `date`-ből kivett adat.
     */
    function getData (date) {
      return `${ DateTime.getDate(date.getTime()) } ${ String.pad(date.getHours()) }`
    }

    const minDate = new Date(min)
    const maxDateData = getData(new Date(max))

    let addHours = 1

    /**
     * @type {Array<string>}
     */
    const dates = [ getData(minDate) ]

    while (!dates.includes(maxDateData)) {
      const newDate = new Date(minDate)

      newDate.setHours(newDate.getHours() + addHours)

      dates.push(getData(newDate))

      addHours++
    }

    return dates
  }

  /**
   * Visszaadja a `min` és `max` közötti hónapokat.
   * @param {string} min - A minimum DateTime.
   * @param {string} max - A maximum DateTime.
   * @returns {ReadonlyArray<string>} A megadott DateTime-ok közötti hónapok.
   */
  static getMonthsBetweenDates (min, max) {
    /**
     * A `date` időpontból kiveszi az évet és a hónapot.
     * @param {Date} date - Az aktuális időpont.
     * @returns {string} A `date`-ből kivett adat.
     */
    function getData (date) {
      return `${ date.getFullYear() }-${ String.pad(date.getMonth() + 1) }`
    }

    const minDate = new Date(min)
    const maxDateData = getData(new Date(max))

    let addMonths = 1

    /**
     * @type {Array<string>}
     */
    const dates = [ getData(minDate) ]

    while (!dates.includes(maxDateData)) {
      const newDate = new Date(minDate)

      newDate.setMonth(newDate.getMonth() + addMonths)

      dates.push(getData(newDate))

      addMonths++
    }

    return dates
  }

  /**
   * Visszaadja a `month` indexhez tartozó hónap nevet.
   * @param {number} monthIndex - A hónap indexe.
   * @returns {string} A megfelelő hónap neve.
   */
  static getMonthName (monthIndex) {
    return MONTHS[monthIndex] || 'December'
  }

  /**
   * A megadott nap a mai nap?
   * @param {string | Date} date - A dátum paraméter.
   * @returns {boolean} Ma már máma van már?
   */
  static isToday (date) {
    const today = new Date()
    const givenDate = is.string(date) ? new Date(date) : date

    const isToday = today.toDateString() === givenDate.toDateString()

    return isToday
  }

  /**
   * Időzóna alapján a különbség ms-ben.
   * @param {Date} date - A dátum, ami alapján a timeZoneOffset-et nézzük.
   * @returns {number} A különbség ms.
   */
  static getTimezoneOffsetDiffInMs (date = new Date()) {
    const localOffset = date.getTimezoneOffset()

    return (UTC_OFFSET - localOffset) * MINUTE_IN_MS
  }

  /**
   * Az excelben a dátumot reprezentáló számot alakítja vissza dátummá.
   * @param {number} excelNumber - Az exceltől kapott érték.
   * @returns {Date} Dátum.
   */
  static getDateFromExcelNumber (excelNumber) {
    const utcDays = Math.floor(excelNumber - 25569) // Az 1900.01.01-től eltelt napok
    const utcMilliseconds = utcDays * 86400 * 1000 // 1 nap = 86400 másodperc = 86400 000 millisecond
    const date = new Date(utcMilliseconds)

    // Az Excel belsően szökőév miatt tartalmaz egy "extra" napot (1900.02.29),
    // ezért a korábbi dátumoknál hozzá kell adni egy napot.
    if (excelNumber < 60) {
      date.setDate(date.getDate() + 1)
    }

    return date
  }
}

DateTime.now = Date.now
DateTime.getDate = getDate
DateTime.getFullDate = getFullDate
DateTime.addSubDays = addSubDays
DateTime.isValidDate = isValidDate
DateTime.getTime = getTime
DateTime.getYear = getYear
DateTime.getDaysCountBetweenDates = getDaysCountBetweenDates
DateTime.getCurrentDateWithFirstDay = getCurrentDateWithFirstDay
DateTime.getDaysBetweenDates = getDaysBetweenDates
DateTime.addSubDaysDate = addSubDaysDate
DateTime.addSubDaysFullDate = addSubDaysFullDate
DateTime.getYearsBetweenDates = getYearsBetweenDates
DateTime.getDatePartsBetweenDates = getDatePartsBetweenDates
DateTime.getDiffMs = getDiffMs
DateTime.getWeeksBetweenDates = getWeeksBetweenDates
DateTime.SECOND_IN_MS = SECOND_IN_MS
DateTime.MINUTE_IN_MS = MINUTE_IN_MS
DateTime.HOUR_IN_MS   = HOUR_IN_MS
DateTime.DAY_IN_MS    = DAY_IN_MS
DateTime.WEEK_IN_MS   = WEEK_IN_MS
DateTime.MONTH_IN_MS  = MONTH_IN_MS
DateTime.DAYS_OF_WEEK = DAYS_OF_WEEK
DateTime.DAYS_OF_YEAR = DAYS_OF_YEAR
DateTime.SECONDS_IN_HOUR = SECONDS_IN_HOUR

module.exports.DateTime = DateTime
